import React, { useEffect, useRef } from 'react';

export const BackgroundCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    let mouseX = -1000;
    let mouseY = -1000;

    const handleResize = () => {
      if (!canvas) return;
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };

    const handleMouseMove = (e: MouseEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);

    interface Particle {
      x: number;
      y: number;
      baseX: number;
      baseY: number;
      vx: number;
      vy: number;
      radius: number;
      darkColor: string;
      lightColor: string;
      alpha: number;
    }

    const particles: Particle[] = [];
    const count = Math.min(Math.floor(width / 20), 80);
    const darkColors = ['rgba(0, 242, 254,', 'rgba(127, 0, 255,', 'rgba(59, 130, 246,', 'rgba(0, 230, 118,'];
    const lightColors = ['rgba(2, 132, 199,', 'rgba(29, 78, 216,', 'rgba(109, 40, 217,', 'rgba(21, 128, 61,'];

    for (let i = 0; i < count; i++) {
      const rx = Math.random() * width;
      const ry = Math.random() * height;
      const colorIdx = Math.floor(Math.random() * darkColors.length);
      particles.push({
        x: rx,
        y: ry,
        baseX: rx,
        baseY: ry,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        radius: Math.random() * 2.2 + 0.8,
        darkColor: darkColors[colorIdx],
        lightColor: lightColors[colorIdx],
        alpha: Math.random() * 0.45 + 0.15
      });
    }

    let animationFrameId: number;

    const render = () => {
      ctx.clearRect(0, 0, width, height);

      const isLightMode = document.documentElement.getAttribute('data-theme') === 'light';

      // Draw subtle ambient gradient mesh spots adapted to light/dark mode
      const g1Color = isLightMode ? 'rgba(2, 132, 199, 0.05)' : 'rgba(0, 242, 254, 0.04)';
      const g2Color = isLightMode ? 'rgba(109, 40, 217, 0.05)' : 'rgba(127, 0, 255, 0.05)';
      const strokeColorPrefix = isLightMode ? '29, 78, 216' : '0, 242, 254';

      const gradient1 = ctx.createRadialGradient(width * 0.2, height * 0.25, 0, width * 0.2, height * 0.25, 450);
      gradient1.addColorStop(0, g1Color);
      gradient1.addColorStop(1, 'transparent');
      ctx.fillStyle = gradient1;
      ctx.fillRect(0, 0, width, height);

      const gradient2 = ctx.createRadialGradient(width * 0.8, height * 0.65, 0, width * 0.8, height * 0.65, 500);
      gradient2.addColorStop(0, g2Color);
      gradient2.addColorStop(1, 'transparent');
      ctx.fillStyle = gradient2;
      ctx.fillRect(0, 0, width, height);

      // Update & render particles
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;

        // Wrap around bounds
        if (p.x < 0) p.x = width;
        if (p.x > width) p.x = 0;
        if (p.y < 0) p.y = height;
        if (p.y > height) p.y = 0;

        // Gentle cursor push effect
        const dx = mouseX - p.x;
        const dy = mouseY - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 140) {
          const force = (140 - dist) / 140;
          p.x -= (dx / dist) * force * 3;
          p.y -= (dy / dist) * force * 3;
        }

        // Draw particle dot
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        const pColor = isLightMode ? p.lightColor : p.darkColor;
        ctx.fillStyle = `${pColor} ${p.alpha})`;
        ctx.fill();

        // Connect nearby nodes with subtle lines
        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];
          const ndx = p.x - p2.x;
          const ndy = p.y - p2.y;
          const ndist = Math.sqrt(ndx * ndx + ndy * ndy);

          if (ndist < 130) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            const lineAlpha = (isLightMode ? 0.16 : 0.12) * (1 - ndist / 130);
            ctx.strokeStyle = `rgba(${strokeColorPrefix}, ${lineAlpha})`;
            ctx.lineWidth = 0.65;
            ctx.stroke();
          }
        }
      }

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      id="bg-canvas"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 0,
        pointerEvents: 'none'
      }}
    />
  );
};
